import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import Header from "@/components/Header";
import { Checkbox } from "@/components/ui/checkbox";
import PasswordStrength, { passwordOk } from "@/components/PasswordStrength";
import { formatApiError } from "@/lib/errors";

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  let submitLabel = isLogin ? "Sign in" : "Create account";
  if (loading) submitLabel = "…";

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && !passwordOk(password)) {
      toast.error("Password needs 8+ characters with a letter and a number");
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password, remember);
      } else {
        await register(email, password, name);
      }
      toast.success(isLogin ? "Welcome back" : "Account created — check your email to confirm it");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err, "Auth failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-md mx-auto px-4 py-16">
        <Card className="tactile-card p-8 rounded-md" data-testid="auth-card">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#00E676]">{isLogin ? "SIGN IN" : "CREATE ACCOUNT"}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-2">
            {isLogin ? "Welcome back." : "Find your leaks."}
          </h1>
          <p className="text-sm text-[#A0A7B0] mt-2">
            {isLogin ? "Log in to keep clawing back." : "Free trial dashboard with your demo leaks. No card yet."}
          </p>
          <form className="space-y-4 mt-6" onSubmit={submit}>
            {!isLogin && (
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" data-testid="auth-input-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="auth-input-email" value={email} required onChange={(e) => setEmail(e.target.value)} placeholder="you@gmail.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {isLogin && <Link to="/forgot-password" className="text-xs text-[#00E676] hover:underline" data-testid="auth-forgot-link">Forgot password?</Link>}
              </div>
              <Input id="password" type="password" data-testid="auth-input-password" value={password} required minLength={isLogin ? 1 : 8} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
              {!isLogin && <PasswordStrength password={password} />}
            </div>
            {isLogin && (
              <label className="flex items-center gap-2 text-sm text-[#A0A7B0] cursor-pointer select-none">
                <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="auth-remember-me" />
                Keep me signed in for 30 days
              </label>
            )}
            <Button type="submit" className="btn-emerald w-full h-11" disabled={loading} data-testid="auth-submit-button">
              {submitLabel}
            </Button>
          </form>
          <div className="mt-6 text-sm text-[#A0A7B0] text-center">
            {isLogin ? (
              <>Don't have an account? <Link to="/register" className="text-[#00E676] hover:underline" data-testid="auth-switch-register">Create one</Link></>
            ) : (
              <>Already have an account? <Link to="/login" className="text-[#00E676] hover:underline" data-testid="auth-switch-login">Sign in</Link></>
            )}
          </div>
          <div className="mt-4 text-xs text-[#606872] text-center" data-testid="auth-legal-links">
            By continuing you agree to our <Link to="/terms" className="underline hover:text-[#A0A7B0]" data-testid="auth-terms-link">Terms of Service</Link> and <Link to="/privacy" className="underline hover:text-[#A0A7B0]" data-testid="auth-privacy-link">Privacy Policy</Link>.
          </div>
        </Card>
      </div>
    </div>
  );
}
